/**
 * AI Capability Registry
 *
 * Single source of truth for every LLM touchpoint in Priorities.ai.
 * Canon authority: DOCS_V4/00-canon/24-ai-capability-taxonomy-and-registry.v1.md
 *
 * Classify each capability on four orthogonal dimensions:
 *   - role (what job the LLM does)
 *   - evidenceModel (whether the outcome depends on the model)
 *   - invocationTier (how it is triggered and governed)
 *   - capabilityClass (DEGAP grouping from AI_ASSISTED_ENHANCEMENTS_SPEC)
 */

import type { LucideIcon } from 'lucide-react';
import type { ElevationLevel } from '../elevationAccess';
import {
  Bot,
  GitMerge,
  Ruler,
  PenLine,
  Network,
  Swords,
  Compass,
  KeyRound,
  Crosshair,
  ShieldAlert,
  RadioTower,
  CalendarClock,
  Sparkles,
  Type,
  Layers,
  BarChart3,
  Scale,
  Brain,
  FileText,
  Wrench,
  ArrowLeftRight,
  Target,
  Mic,
  Headphones,
  Map,
  Quote,
} from 'lucide-react';

// ── Taxonomy types ───────────────────────────────────────────────────────────

export type AiCapabilityRole =
  | 'classifier'
  | 'extractor'
  | 'generator'
  | 'detector'
  | 'narrator'
  | 'advisor';

export type AiEvidenceModel =
  | 'llm_primary'
  | 'deterministic_narration'
  | 'deterministic_only';

export type AiInvocationTier =
  | 'workspace_agent'
  | 'inline_assist'
  | 'on_demand_narrator'
  | 'embedded_advisor';

export type AiCapabilityClass =
  | 'shaping'
  | 'criteria'
  | 'tool'
  | 'priority_reasoning'
  | 'governance_drift';

export type AiDegapPhase =
  | 'decide'
  | 'engage'
  | 'gather'
  | 'arrange'
  | 'prioritize'
  | 'cross_cutting';

export type AiTransport = 'call_agent_llm' | 'call_feature_llm';

export interface AiCapabilityMeta {
  /** Stable snake_case identifier. */
  id: string;
  name: string;
  tagline: string;
  description: string;
  role: AiCapabilityRole;
  evidenceModel: AiEvidenceModel;
  invocationTier: AiInvocationTier;
  capabilityClass: AiCapabilityClass;
  degapPhases: AiDegapPhase[];
  transport: AiTransport;
  /**
   * Vite env flag name. When set to `'false'`, capability is off regardless of
   * workspace assist settings. Omit when always on (subject to provider config).
   */
  envFlag?: string;
  defaultEnabled: boolean;
  /** Settings route for workspace agents or per-capability config. */
  settingsHref?: string;
  reportHref?: string;
  /** Short summary for trust docs — what content is sent to the model. */
  trustPayloadSummary: string;
  /** Primary service module implementing the capability. */
  serviceModule: string;
  /** Links to agent_settings.agent_type when applicable. */
  agentType?: string;
  /**
   * Standing dimension (Doc 24 §2.5). Default worker.
   * Participant = Voice / Agents as Users (gated by agents_as_users).
   */
  standing?: 'worker' | 'participant';
  /**
   * Set when the capability serves only pages behind the Elevation gate
   * (lib/elevationAccess.ts): 'owner' for Elevation's own pages, 'team' for
   * the team's pages. It is then listed in settings and runs only for people
   * at that level; nobody else is shown a switch for a page they cannot open.
   * Omit for every product capability. See isAiCapabilityOpenTo.
   */
  elevationLevel?: ElevationLevel;
  icon: LucideIcon;
  /** Tailwind accent for settings cards. */
  accent: { iconBg: string; iconText: string };
}

// ── Registry entries ───────────────────────────────────────────────────────────

export const AI_CAPABILITIES: Record<string, AiCapabilityMeta> = {
  // ── Workspace agents: item-shaping ─────────────────────────────────────────
  atc: {
    id: 'atc',
    name: 'ATC — Air Traffic Controller',
    tagline: 'Classifies items by type and altitude',
    description:
      'Classifies each item by type (problem / opportunity / risk / solution / commitment / investment) and recommends an altitude level (1–5). Proposals require human acceptance.',
    role: 'classifier',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/atc-agent',
    reportHref: '/prioritization/agents/atc',
    trustPayloadSummary: 'Item title, description, admin reference documents (category examples, altitude guidance)',
    serviceModule: 'src/lib/llmAirTrafficControllerService.ts',
    agentType: 'atc',
    icon: Bot,
    accent: { iconBg: 'bg-blue-100', iconText: 'text-blue-600' },
  },
  twins: {
    id: 'twins',
    name: 'Twins',
    tagline: 'Finds duplicate and variant items',
    description:
      'Identifies groups of duplicate or variant items across the backlog in batches of 40. Surfaces clusters for human merge or dismiss decisions.',
    role: 'detector',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/twins-agent',
    reportHref: '/prioritization/agents/twins',
    trustPayloadSummary: 'Item titles and descriptions only (no scores, votes, or identity data)',
    serviceModule: 'src/lib/llmTwinsService.ts',
    agentType: 'twins',
    icon: GitMerge,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
  },
  scribe: {
    id: 'scribe',
    name: 'Scribe',
    tagline: 'Parses free-form intake into structured items',
    description:
      'Parses free-form typed or voice-transcribed text into structured item proposals (title, description, category, altitude, relationship hints).',
    role: 'extractor',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/scribe-agent',
    trustPayloadSummary: 'Raw text typed or dictated by the user',
    serviceModule: 'src/lib/llmScribeService.ts',
    agentType: 'scribe',
    icon: PenLine,
    accent: { iconBg: 'bg-violet-100', iconText: 'text-violet-600' },
  },
  nexus_interactive: {
    id: 'nexus_interactive',
    name: 'Nexus — Interactive',
    tagline: 'Suggests relationship types from stated intent',
    description:
      'Suggests the correct relationship type (dependency, breakdown, elaboration, etc.) based on the user\'s stated intent for the current item.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/nexus-agent',
    trustPayloadSummary: 'Current item title and description; the user\'s intent statement',
    serviceModule: 'src/lib/llmNexusService.ts',
    agentType: 'nexus',
    icon: Network,
    accent: { iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
  },
  nexus_batch: {
    id: 'nexus_batch',
    name: 'Nexus — Batch',
    tagline: 'Scans backlog for structural relationships',
    description:
      'Autonomously scans the backlog for structural relationship candidates between item pairs in batches of 25.',
    role: 'detector',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/nexus-agent',
    reportHref: '/prioritization/agents/nexus',
    trustPayloadSummary: 'Item titles and descriptions in batches of 25',
    serviceModule: 'src/lib/llmNexusBatchService.ts',
    agentType: 'nexus',
    icon: Network,
    accent: { iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
  },
  value_estimator: {
    id: 'value_estimator',
    name: 'Value Estimator',
    tagline: 'Proposes a value for an item, with the evidence it came from',
    description:
      'Proposes an attribute value (effort first) for an item from the item text, this workspace\'s accepted values on similar items, and administrator reference documents. Abstains and names the missing input when the item is too thin. A person accepts, changes, or rejects every proposal.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: false,
    settingsHref: '/prioritization/settings/value-estimator-agent',
    reportHref: '/prioritization/agents/value_estimator',
    trustPayloadSummary:
      'Item title, description, category and altitude; the attribute definition and scale; titles, descriptions and accepted values of similar items in the same workspace; administrator reference documents. Never session scores or participant identities.',
    serviceModule: 'src/lib/llmValueEstimatorService.ts',
    agentType: 'value_estimator',
    icon: Ruler,
    accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-700' },
  },

  harry: {
    id: 'harry',
    name: 'Harry',
    tagline: 'Workspace guide — routes you to the right job',
    description:
      'Answers plain-language questions about what to do in the workspace, routes to capabilities and agents, and optionally speaks in Harry Max\'s voice.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['cross_cutting'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/harry-agent',
    trustPayloadSummary: 'User question, current page context, capability catalog summaries (no item content by default)',
    serviceModule: 'src/lib/llmHarryService.ts',
    agentType: 'harry',
    icon: Headphones,
    accent: { iconBg: 'bg-sky-100', iconText: 'text-sky-600' },
  },
  wes: {
    id: 'wes',
    name: 'Wes',
    tagline: 'Co-founder guide — product and workflow advice',
    description:
      'Answers in Wes Bright\'s voice with practical, product-grounded guidance. Available from the help panel assistant selector alongside Harry and Concierge.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'workspace_agent',
    capabilityClass: 'shaping',
    degapPhases: ['cross_cutting'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/wes-agent',
    trustPayloadSummary: 'User question, current page context, capability catalog summaries (no item content by default)',
    serviceModule: 'src/lib/llmWesService.ts',
    agentType: 'wes',
    icon: Sparkles,
    accent: { iconBg: 'bg-indigo-100', iconText: 'text-indigo-600' },
  },

  // ── Workspace agents: intelligence (deterministic + narration) ───────────
  cassandra: {
    id: 'cassandra',
    name: 'Fracture — Decision Red-Team',
    tagline: 'Exposes fragile rankings before you publish',
    description:
      'Stress-tests settled rankings using exact math over session scores. LLM narrates pre-computed fragility findings only.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/fracture-agent',
    reportHref: '/prioritization/fracture',
    trustPayloadSummary: 'Finding type, numeric evidence (score gaps, weight thresholds, dissent %), item names — no raw scores in prompt',
    serviceModule: 'src/lib/intelligence/cassandraAnalyzer.ts',
    agentType: 'cassandra',
    icon: Swords,
    accent: { iconBg: 'bg-rose-100', iconText: 'text-rose-600' },
  },
  compass: {
    id: 'compass',
    name: 'Compass — Strategy Alignment',
    tagline: 'Catches where priorities drift from strategy',
    description:
      'Compares strategic objectives to priority mappings deterministically. LLM narrates alignment gaps only.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['decide', 'prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/compass-agent',
    reportHref: '/prioritization/compass',
    trustPayloadSummary: 'Finding type, objective names, alignment gap description — no item text',
    serviceModule: 'src/lib/intelligence/compassAnalyzer.ts',
    agentType: 'compass',
    icon: Compass,
    accent: { iconBg: 'bg-sky-100', iconText: 'text-sky-600' },
  },
  keystone: {
    id: 'keystone',
    name: 'Keystone — Leverage Finder',
    tagline: 'Surfaces items that unblock everything else',
    description:
      'Reads the dependency graph deterministically to find keystones and cycles. LLM narrates leverage findings only.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['gather', 'prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/keystone-agent',
    reportHref: '/prioritization/keystone',
    trustPayloadSummary: 'Finding type, keystone item name, reach count, or cycle description — no item text or scores',
    serviceModule: 'src/lib/intelligence/keystoneAnalyzer.ts',
    agentType: 'keystone',
    icon: KeyRound,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
  },
  meridian: {
    id: 'meridian',
    name: 'Meridian — Execution Drift',
    tagline: 'Reconciles the ratified ranking with real activity',
    description:
      'Joins the ratified priority ranking against 30 days of connector change events deterministically. LLM narrates commitment-vs-attention divergence only, framed as prompts for inquiry.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['decide', 'cross_cutting'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/meridian-agent',
    reportHref: '/prioritization/meridian',
    trustPayloadSummary: 'Finding type, item names, ranks, attention-share percentages and event counts — no event payloads or external-system content',
    serviceModule: 'src/lib/intelligence/meridianAnalyzer.ts',
    agentType: 'meridian',
    icon: Crosshair,
    accent: { iconBg: 'bg-violet-100', iconText: 'text-violet-600' },
  },
  condition_watch: {
    id: 'condition_watch',
    name: 'Condition Watch — Governance Sentinel',
    tagline: 'Flags candidate Condition B/C moments for human review',
    description:
      'Cross-references recorded signals (agent findings, impact events, stakeholder pulses) against governance acts (rescorings, reprioritization triggers, catchball, ratifications) deterministically. Flags Condition B candidates (a material signal nobody acted on) and Condition C candidates (a plan change with no signal behind it) as questions for the humans holding the right — never verdicts. It never sets any condition: the POR condition is set only by a human via set_por_condition, rationale required, never inferred.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['decide', 'cross_cutting'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/condition-watch-agent',
    reportHref: '/prioritization/condition-watch',
    trustPayloadSummary: 'Finding type, signal/act kinds and short labels, dates, and gap-in-days — no scores, no full item or event content',
    serviceModule: 'src/lib/intelligence/conditionWatchAnalyzer.ts',
    agentType: 'condition_watch',
    icon: ShieldAlert,
    accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-600' },
  },
  review_due: {
    id: 'review_due',
    name: 'Review Due — Reconsideration Watcher',
    tagline: 'Surfaces the review dates the record set for itself',
    description:
      'Scans the review clocks the record already carries — decision review-by dates, armed commitment terms, confirmed signals past review with no response, typed conditions outside their bound or unchecked — and puts what is due on the docket as a candidate finding. Exact dates and statuses only; no model. It proposes only: it never trips a term, reopens a decision or answers a signal.',
    role: 'detector',
    evidenceModel: 'deterministic_only',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['decide', 'cross_cutting'],
    // No model is called; the field names the client path its runner shares with its siblings.
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/review-due-agent',
    reportHref: '/prioritization/agents/review_due',
    trustPayloadSummary: 'Nothing leaves the workspace: no model is used',
    serviceModule: 'src/lib/intelligence/reviewDueAnalyzer.ts',
    agentType: 'review_due',
    icon: CalendarClock,
    accent: { iconBg: 'bg-[var(--slate-dim)]', iconText: 'text-[var(--text-sec)]' },
  },
  beacon: {
    id: 'beacon',
    name: 'Beacon — External Signal Watcher',
    tagline: 'Watches external sources for what the ratified plan depends on',
    description:
      'Derives a watchlist of external entities (competitors, technologies, regulations, customers, markets) from ratified item titles, strategies, and plan assumptions via one metered LLM extraction pass — every entry reviewable and dismissable — then sweeps external news server-side on the Ambient Runtime schedule. Signals and findings are deterministic: a finding is raised only when a fetched signal ties to a currently ratified item or a valid assumption, always with the evidence link. Silent otherwise — a moment, not a feed.',
    role: 'detector',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'workspace_agent',
    capabilityClass: 'governance_drift',
    degapPhases: ['decide', 'cross_cutting'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/beacon-agent',
    reportHref: '/prioritization/agents/beacon',
    trustPayloadSummary: 'Watchlist derivation only: ratified item titles, strategy/objective names, and assumption statements — no scores, no participants, no external page content',
    serviceModule: 'src/lib/intelligence/beaconAnalyzer.ts',
    agentType: 'beacon',
    icon: RadioTower,
    accent: { iconBg: 'bg-indigo-100', iconText: 'text-indigo-600' },
  },

  // ── Inline assists ─────────────────────────────────────────────────────────
  title_generator: {
    id: 'title_generator',
    name: 'Title generator',
    tagline: 'Suggests a concise title from description',
    description:
      'Generates a suggested item title from the in-progress description. Triggered inline on item detail and Create Item.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    trustPayloadSummary: 'In-progress item title and description',
    serviceModule: 'src/lib/llmTitleService.ts',
    icon: Type,
    accent: { iconBg: 'bg-indigo-100', iconText: 'text-indigo-600' },
  },
  idea_crafting: {
    id: 'idea_crafting',
    name: 'Descriptor',
    tagline: 'Writes title or description from the other',
    description:
      'Suggests a clearer, more actionable title and description from whichever side the user already has. Surfaced as the Descriptor agent on Item Detail, and inline on Create Item / Submit Idea.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_IDEA_CRAFTING',
    defaultEnabled: true,
    trustPayloadSummary: 'In-progress item title and description being drafted',
    serviceModule: 'src/lib/llmIdeaCraftingService.ts',
    icon: Sparkles,
    accent: { iconBg: 'bg-purple-100', iconText: 'text-purple-600' },
  },
  shaping: {
    id: 'shaping',
    name: 'Item work (six categories)',
    tagline: 'Rewrites one idea in six candidate framings',
    description:
      'Rewrites a single idea in all six candidate framings (problem, opportunity, risk, solution, commitment, investment).',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_SHAPING',
    defaultEnabled: true,
    trustPayloadSummary: 'Item title and description',
    serviceModule: 'src/lib/llmShapingService.ts',
    icon: Layers,
    accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-600' },
  },
  lexicon_analysis: {
    id: 'lexicon_analysis',
    name: 'Lexicon analysis',
    tagline: 'Analyzes meeting transcripts for communication patterns',
    description:
      'Scores speakers on communication dimensions and surfaces NLP meta-model patterns from transcripts.',
    role: 'extractor',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'shaping',
    degapPhases: ['engage', 'cross_cutting'],
    transport: 'call_agent_llm',
    defaultEnabled: true,
    trustPayloadSummary: 'Meeting transcript text provided by the user',
    serviceModule: 'src/lib/lexiconAnalysisService.ts',
    icon: Mic,
    accent: { iconBg: 'bg-fuchsia-100', iconText: 'text-fuchsia-600' },
  },
  outcome_driver_generator: {
    id: 'outcome_driver_generator',
    name: 'Outcome driver generator',
    tagline: 'Structures outcome drivers from free-form input',
    description:
      'Generates structured outcome driver candidates from free-form input. Falls back to rule-based classification when LLM is off.',
    role: 'extractor',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'shaping',
    degapPhases: ['decide'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    trustPayloadSummary: 'User-provided input text describing desired outcomes',
    serviceModule: 'src/features/outcome-drivers/llmService.ts',
    icon: Target,
    accent: { iconBg: 'bg-orange-100', iconText: 'text-orange-600' },
  },

  // ── On-demand narrators ────────────────────────────────────────────────────
  results_explainer: {
    id: 'results_explainer',
    name: 'Results explainer',
    tagline: 'Writes the prioritization memo and explains session results',
    description:
      'Writes the prioritization memo when results are published — why the items above the line ranked where they did, what was traded off, and what just missed — and the report templates. Reads the scores and criteria, the reasons people recorded, and the session transcript. Recorded words are quoted from the record, never written by the model. The one generator for explaining a ranking (owner ruling K5, 2026-10-14).',
    role: 'narrator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_RESULTS_EXPLAINER',
    defaultEnabled: true,
    trustPayloadSummary:
      'Session report: item titles, ranks, scores, criteria and weights, relationship types; the rationale notes and override reasons people recorded; and up to 12,000 characters of the session transcript (pasted, or recorded in the browser)',
    serviceModule: 'src/lib/llmResultsExplainerService.ts',
    icon: FileText,
    accent: { iconBg: 'bg-slate-100', iconText: 'text-slate-600' },
  },
  transcript_item_linker: {
    id: 'transcript_item_linker',
    name: 'Transcript item linker',
    tagline: 'Finds what was said about each item in a session transcript',
    description:
      'Reads a session transcript and proposes, for each ranked item, the passages where someone gave a reason about it. Every passage is copied word for word from the transcript (a proposal whose words are not there is dropped), and none counts until a person in the session confirms it. Confirmed passages are quoted in the prioritization memo and the session report. Brief K slice 2b.',
    role: 'extractor',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'tool',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_TRANSCRIPT_ITEM_LINKER',
    defaultEnabled: true,
    trustPayloadSummary:
      'The session transcript (pasted, or recorded in the browser, with the speaker labels already in it) and the titles and ids of the items the session ranked; no scores, no votes, no other identity data',
    serviceModule: 'src/lib/transcriptItemLinkerService.ts',
    icon: Quote,
    accent: { iconBg: 'bg-slate-100', iconText: 'text-slate-600' },
  },
  aggregation_narrator: {
    id: 'aggregation_narrator',
    name: 'Aggregation narrator',
    tagline: 'Interprets multi-participant aggregation',
    description: 'Interprets convergence, outliers, and polarization in aggregated scores.',
    role: 'narrator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_AGGREGATION',
    defaultEnabled: true,
    trustPayloadSummary: 'Item titles and aggregated score distributions (no individual vote attribution)',
    serviceModule: 'src/lib/llmAggregationService.ts',
    icon: BarChart3,
    accent: { iconBg: 'bg-cyan-100', iconText: 'text-cyan-600' },
  },
  contestation_analyzer: {
    id: 'contestation_analyzer',
    name: 'Contestation analyzer',
    tagline: 'Summarizes value disagreements',
    description: 'Summarizes disagreements when participants propose conflicting attribute values.',
    role: 'narrator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['arrange', 'prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_CONTESTATION',
    defaultEnabled: true,
    trustPayloadSummary: 'Proposed values, rationale text, confidence levels (no participant names)',
    serviceModule: 'src/lib/llmContestationService.ts',
    icon: Scale,
    accent: { iconBg: 'bg-yellow-100', iconText: 'text-yellow-700' },
  },
  cognitive_bias_detector: {
    id: 'cognitive_bias_detector',
    name: 'Cognitive bias detector',
    tagline: 'Flags bias patterns in sessions and portfolios',
    description: 'Identifies patterns indicative of known biases (sunk cost, HiPPO, groupthink, recency).',
    role: 'detector',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'governance_drift',
    degapPhases: ['prioritize', 'cross_cutting'],
    transport: 'call_agent_llm',
    envFlag: 'VITE_ENABLE_LLM_COGNITIVE_BIAS',
    defaultEnabled: true,
    trustPayloadSummary: 'Item titles, session/cycle metadata, ranking patterns (no individual identity)',
    serviceModule: 'src/lib/llmCognitiveBiasService.ts',
    icon: Brain,
    accent: { iconBg: 'bg-red-100', iconText: 'text-red-600' },
  },
  backlog_health_narrator: {
    id: 'backlog_health_narrator',
    name: 'Backlog health narrator',
    tagline: 'Explains backlog health metrics in plain language',
    description: 'Interprets deterministic backlog health metrics. No item content is sent to the model.',
    role: 'narrator',
    evidenceModel: 'deterministic_narration',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'governance_drift',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_BACKLOG_HEALTH',
    defaultEnabled: true,
    trustPayloadSummary: 'Aggregate counts and staleness distributions only — no item content',
    serviceModule: 'src/lib/llmBacklogHealthService.ts',
    icon: BarChart3,
    accent: { iconBg: 'bg-green-100', iconText: 'text-green-600' },
  },
  field_reader: {
    id: 'field_reader',
    name: 'Field reader',
    tagline: 'Narrates Field of Play context',
    description:
      'Generates plain-language narration for the Field of Play, one of the mental-model pages for the Peak Priorities team. Team only.',
    role: 'narrator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_FIELD_READER',
    defaultEnabled: true,
    trustPayloadSummary: 'Field of Play aggregate context (titles and summary metrics)',
    serviceModule: 'src/lib/llmFieldReaderService.ts',
    // Its one caller is the Field of Play (/elevation/field-of-play), a team
    // page since Wes kept the mental models in Priorities.ai (2026-10-18).
    elevationLevel: 'team',
    icon: Map,
    accent: { iconBg: 'bg-lime-100', iconText: 'text-lime-700' },
  },

  // ── Embedded advisors ────────────────────────────────────────────────────
  criteria_recommendation: {
    id: 'criteria_recommendation',
    name: 'Criteria recommendation',
    tagline: 'Suggests scoring attributes for the pool',
    description: 'Suggests which scoring attributes are most likely to differentiate the candidate pool.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'criteria',
    degapPhases: ['arrange'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_CRITERIA_RECOMMENDATION',
    defaultEnabled: true,
    trustPayloadSummary: 'Item titles and attribute names/descriptions (no scores)',
    serviceModule: 'src/lib/llmCriteriaRecommendationService.ts',
    icon: BarChart3,
    accent: { iconBg: 'bg-violet-100', iconText: 'text-violet-600' },
  },
  tool_advisor: {
    id: 'tool_advisor',
    name: 'Tool advisor',
    tagline: 'Recommends prioritization methods',
    description: 'Recommends which prioritization method best fits a session\'s configuration.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'tool',
    degapPhases: ['arrange'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_TOOL_ADVISOR',
    defaultEnabled: true,
    trustPayloadSummary: 'Session metadata, item count, criteria count, tool compatibility flags (no item content)',
    serviceModule: 'src/lib/llmToolAdvisorService.ts',
    icon: Wrench,
    accent: { iconBg: 'bg-gray-100', iconText: 'text-gray-700' },
  },
  start_intent_guide: {
    id: 'start_intent_guide',
    name: 'Start guide',
    tagline: 'Maps a plain-language goal to a starting point',
    description:
      'Reads what someone says they are trying to do and recommends the matching starting point, extracting a suggested title and any options they mentioned.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'shaping',
    degapPhases: ['decide'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_START_GUIDE',
    defaultEnabled: true,
    trustPayloadSummary:
      'The sentence typed into the Start screen plus the intent catalog labels (no workspace content)',
    serviceModule: 'src/apps/start/startGuideService.ts',
    icon: Compass,
    accent: { iconBg: 'bg-gray-100', iconText: 'text-gray-700' },
  },
  sync_advisor: {
    id: 'sync_advisor',
    name: 'Sync advisor',
    tagline: 'Suggests sync conflict resolutions',
    description: 'Suggests conflict resolutions and trust-rule configurations for the sync layer.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'governance_drift',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_SYNC_ADVISOR',
    defaultEnabled: true,
    trustPayloadSummary: 'Conflict field names, value types, and timestamps (no content values)',
    serviceModule: 'src/lib/llmSyncAdvisorService.ts',
    icon: ArrowLeftRight,
    accent: { iconBg: 'bg-stone-100', iconText: 'text-stone-700' },
  },
  signal_routing_advisor: {
    id: 'signal_routing_advisor',
    name: 'Signal routing advisor',
    tagline: 'Proposes what a signal touches',
    description:
      'Given a recorded Signal Event (occurrence + detail), proposes which routing class it touches — feasibility, value, assumption, mandate, or record error — with a one-line reason. Proposal only (P-AGENT-1): a human confirms via classify_signal_event; the advisor never writes a classification. Signal Framework v2 (canon 23 §6.2), SIG-4.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'governance_drift',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_SIGNAL_ROUTING_ADVISOR',
    defaultEnabled: true,
    trustPayloadSummary:
      'Signal occurrence id/text, optional detail line, severity — nothing beyond what the signal record itself holds',
    serviceModule: 'src/lib/llmSignalRoutingAdvisorService.ts',
    icon: ArrowLeftRight,
    accent: { iconBg: 'bg-stone-100', iconText: 'text-stone-700' },
  },
  value_proposal_improver: {
    id: 'value_proposal_improver',
    name: 'Value proposal improver',
    tagline: 'Improves attribute value rationales',
    description: 'Helps write a clearer, better-evidenced rationale for a proposed attribute value.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'criteria',
    degapPhases: ['arrange'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_VALUE_PROPOSAL',
    defaultEnabled: true,
    trustPayloadSummary: 'Proposed value, attribute definition, and user\'s draft rationale text',
    serviceModule: 'src/lib/llmValueProposalService.ts',
    icon: PenLine,
    accent: { iconBg: 'bg-pink-100', iconText: 'text-pink-600' },
  },
  pool_clustering: {
    id: 'pool_clustering',
    name: 'Pool clustering',
    tagline: 'Suggests related items in a pool',
    description: 'Suggests which items may be related, duplicates, or variants for human review in item pools.',
    role: 'detector',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'shaping',
    degapPhases: ['gather'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_CLUSTERING',
    defaultEnabled: true,
    trustPayloadSummary: 'Item titles and descriptions in the active pool',
    serviceModule: 'src/lib/llmClusteringService.ts',
    icon: GitMerge,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
  },
  exchange_drafting: {
    id: 'exchange_drafting',
    name: 'Exchange drafting',
    tagline: 'Drafts Requests, Interpretations, and Proposal annotations — never throws the ball',
    description:
      'Helps the ball-holder in a Catchball loop compose clear Loop Intent (strategic context, asks, constraints, guardrails, response guidance), faithful Interpretations for the receiving tier to edit and own, and well-evidenced Proposal annotations sourced from session results. Drafts fill the local form for human review; ball movement, submission, and acceptance stay human (agents draft, never throw). Surfaces gated by the exchange_drafting workspace module.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'governance_drift',
    degapPhases: ['engage'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    trustPayloadSummary:
      'Loop state, ball position, iteration count, legitimacy marking, the user’s own draft intent text, and the session-result summary being annotated (item titles, ranks, aggregate scores) — no participant identity, no vote attribution',
    serviceModule: 'src/lib/llmExchangeDraftService.ts',
    icon: ArrowLeftRight,
    accent: { iconBg: 'bg-cyan-100', iconText: 'text-cyan-700' },
  },
  co_facilitator: {
    id: 'co_facilitator',
    name: 'Co-Facilitator',
    tagline: 'Methodology coach — how to prioritize, not product navigation',
    description:
      'A floating chat advisor for prioritization methodology: choosing methods, writing rationales, resolving disagreements, and interpreting session results. Distinct from Guides (product navigation). Only one corner FAB is shown at a time.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    trustPayloadSummary: 'Current page context only (no item content)',
    serviceModule: 'src/components/AINavigator.tsx',
    icon: Sparkles,
    accent: { iconBg: 'bg-purple-100', iconText: 'text-purple-600' },
  },
  session_copilot: {
    id: 'session_copilot',
    name: 'Session copilot',
    tagline: 'Facilitation copilot — where the session stands and how to run it',
    description:
      'In-session advisor for facilitators and participants: explains the session\'s state, what the Frame allows right now, and offers optional text drafts (pause note, divergence-discussion framing, clarification prompt) the facilitator may copy. Never acts, scores, overrides, or ranks; refuses early-ranking asks by naming the incomplete stages. Slice 2 of the AI platform brief, additionally gated by the session_copilot workspace module + flag.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'embedded_advisor',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    trustPayloadSummary:
      'Session id/name/state and run mode, derived Frame stage summary, participant count, Voice seat names/seat types/counting policies, criteria lock timestamp, item pool count, retrieved canon section excerpts, and the user\'s question (no item content, no scores)',
    serviceModule: 'src/lib/llmSessionCopilotService.ts',
    standing: 'worker',
    icon: Compass,
    accent: { iconBg: 'bg-teal-100', iconText: 'text-teal-700' },
  },
  mandate_hypothesis_reflection: {
    id: 'mandate_hypothesis_reflection',
    name: 'Mandate hypothesis reflection',
    tagline: 'Names the organizing theme of a Day 100 hypothesis',
    description:
      'Reflects back the single organizing theme of a leader\'s Day 100 outcome hypothesis as they write or revise it in the Mandate transition module — "What I\'m hearing is…". Advisory only; never edits the hypothesis text.',
    role: 'narrator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_MANDATE_REFLECTION',
    defaultEnabled: true,
    trustPayloadSummary: 'The in-progress Day 100 hypothesis text and the transition\'s STaRS situation type',
    serviceModule: 'src/lib/llmMandateService.ts',
    icon: Target,
    accent: { iconBg: 'bg-indigo-100', iconText: 'text-indigo-600' },
  },
  mandate_priority_connection: {
    id: 'mandate_priority_connection',
    name: 'Mandate priority ↔ outcome connection',
    tagline: 'Names the causal link between a new priority and the Day 100 outcome',
    description:
      'When adding a priority in the Mandate Priorities Stack, names whether and how the priority moves the leader toward (or away from) their current Day 100 outcome hypothesis. Advisory only; shown as an inline callout the user reads before saving.',
    role: 'advisor',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_MANDATE_REFLECTION',
    defaultEnabled: true,
    trustPayloadSummary: 'Draft priority title and rationale, and the transition\'s current Day 100 outcome hypothesis',
    serviceModule: 'src/lib/llmMandateService.ts',
    icon: Compass,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-700' },
  },

  // ── Participant standing (Agents as Users / Voices) ────────────────────────
  voice_advisory_valuation: {
    id: 'voice_advisory_valuation',
    name: 'Voice Constituency Perspective',
    tagline: 'Constituency Voice commentary + citations from its corpus',
    description:
      'Generates constituency perspective (summary, per-item commentary, corpus citations) for a seated Voice. No scores or ranks. Excluded from the counted aggregate. Voice, never authority.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/voices',
    trustPayloadSummary: 'Corpus document titles/bodies + session item titles (no human participant scores)',
    serviceModule: 'src/features/voices/voiceAdvisoryService.ts',
    standing: 'participant',
    icon: Mic,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-700' },
  },
  voice_estimator_valuation: {
    id: 'voice_estimator_valuation',
    name: 'Voice Estimator Seat',
    tagline: 'Counted per-item estimates + one-line rationales from a Voice corpus',
    description:
      'Generates per-item numeric estimates (0-100) with one-line corpus-grounded rationales for a Voice seated as an estimator with facilitator-opted-in Counted policy (Voices Phase 2, voices_counted flag). Counted output joins the counted aggregate — always badged — and accumulates a calibration ledger; session results still pass human acceptance and ratification. Voice, never authority.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'on_demand_narrator',
    capabilityClass: 'priority_reasoning',
    degapPhases: ['prioritize'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/voices',
    trustPayloadSummary:
      'Corpus document titles/bodies + session item titles/descriptions and criteria summary (no human participant scores)',
    serviceModule: 'src/features/voices/voiceEstimatorService.ts',
    standing: 'participant',
    icon: Mic,
    accent: { iconBg: 'bg-amber-100', iconText: 'text-amber-700' },
  },

  // ── Inline assists: query authoring ────────────────────────────────────────
  nl_to_pql: {
    id: 'nl_to_pql',
    name: 'AI query authoring',
    tagline: 'Turns plain English into a PQL statement',
    description:
      'Translates a plain-English request into a PQL (Priorities Query Language) statement for the item list and the Report Designer (the same statement bar on both). The statement always lands in the editable statement bar and is validated by the deterministic parser before it ever filters anything — the model proposes text only, never evaluates a query or applies a filter directly. Also powers "Fix with AI" on statements that fail to parse, and the ⌘K command bar\'s "Ask" row, which lands the translated statement in the same bar via ?pql=. Quality is measured by the golden-set eval (npm run eval:pql).',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'tool',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    envFlag: 'VITE_ENABLE_LLM_NL_TO_PQL',
    defaultEnabled: true,
    trustPayloadSummary: 'The typed English request (or the invalid statement + parser error, for Fix with AI) plus the item fields\' names, types, and option values (the item list and the Report Designer read the same fields) — no item content, no scores, no identity data',
    serviceModule: 'src/lib/pql/ai/nlToPql.ts',
    icon: Wrench,
    accent: { iconBg: 'bg-emerald-100', iconText: 'text-emerald-700' },
  },
  agent_from_words: {
    id: 'agent_from_words',
    name: 'Say an agent',
    tagline: 'Turns a sentence into a draft for the agent creator',
    description:
      'In Settings → Agents → Create an agent, a workspace admin says what the agent should do; the model proposes which of the four agent types fits, a name, a one-line description, the agent\'s own words and, for the estimate type, which value it works on. A pure clamp in the browser keeps the draft to those fields: it never adds an output kind, an evidence source, an event, a reviewer, bulk accept, the standing rule, a model or a limit, and a request no type can honor is shown with its next step instead of being kept quietly. The person edits every field and presses Create; nothing is saved by the model.',
    role: 'generator',
    evidenceModel: 'llm_primary',
    invocationTier: 'inline_assist',
    capabilityClass: 'tool',
    degapPhases: ['cross_cutting'],
    transport: 'call_feature_llm',
    defaultEnabled: true,
    settingsHref: '/prioritization/settings/custom-agents',
    trustPayloadSummary: 'The administrator\'s sentence, the four agent type names with their summaries and examples, and the names of the workspace\'s values (for example "Effort") — no item content, no scores, no personal data',
    serviceModule: 'src/lib/ai/agentFromWords.ts',
    icon: Wrench,
    accent: { iconBg: 'bg-emerald-100', iconText: 'text-emerald-700' },
  },
};

// ── Derived lists ──────────────────────────────────────────────────────────────

export const AI_CAPABILITY_LIST: AiCapabilityMeta[] = Object.values(AI_CAPABILITIES);

export const WORKSPACE_AGENT_CAPABILITIES = AI_CAPABILITY_LIST.filter(
  (c) => c.invocationTier === 'workspace_agent',
);

export const AI_ASSIST_CAPABILITIES = AI_CAPABILITY_LIST.filter(
  (c) =>
    c.invocationTier === 'inline_assist' ||
    c.invocationTier === 'on_demand_narrator' ||
    c.invocationTier === 'embedded_advisor',
);

export function getAiCapability(id: string): AiCapabilityMeta | undefined {
  return AI_CAPABILITIES[id];
}

export function capabilitiesByClass(
  cls: AiCapabilityClass,
): AiCapabilityMeta[] {
  return AI_CAPABILITY_LIST.filter((c) => c.capabilityClass === cls);
}

export function capabilitiesByTier(
  tier: AiInvocationTier,
): AiCapabilityMeta[] {
  return AI_CAPABILITY_LIST.filter((c) => c.invocationTier === tier);
}

/** Human-readable role label for settings UI. */
export const AI_ROLE_LABELS: Record<AiCapabilityRole, string> = {
  classifier: 'Classifier',
  extractor: 'Extractor',
  generator: 'Generator',
  detector: 'Detector',
  narrator: 'Narrator',
  advisor: 'Advisor',
};

export const AI_TIER_LABELS: Record<AiInvocationTier, string> = {
  workspace_agent: 'Workspace agent',
  inline_assist: 'Inline assist',
  on_demand_narrator: 'On-demand narrator',
  embedded_advisor: 'Embedded advisor',
};

export const AI_EVIDENCE_LABELS: Record<AiEvidenceModel, string> = {
  llm_primary: 'LLM-primary',
  deterministic_narration: 'Deterministic + narration',
  deterministic_only: 'Deterministic only',
};

/** Invocation tiers shown on the AI Assists settings page (excludes workspace_agent). */
export const AI_ASSIST_TIERS: AiInvocationTier[] = [
  'inline_assist',
  'on_demand_narrator',
  'embedded_advisor',
];

/** Ordered sections for the AI Assists catalog UI. */
export const AI_ASSIST_TIER_SECTIONS: {
  tier: AiInvocationTier;
  title: string;
  description: string;
}[] = [
  {
    tier: 'inline_assist',
    title: 'Inline assists',
    description:
      'Triggered in context while drafting items, frames, or transcripts. No batch runs or agent logs.',
  },
  {
    tier: 'on_demand_narrator',
    title: 'On-demand narrators',
    description:
      'Explain results when a user clicks a button in reports or session views. Read-only — never changes state.',
  },
  {
    tier: 'embedded_advisor',
    title: 'Embedded advisors',
    description:
      'Contextual recommendations in arrange flows, sync conflicts, and attribute proposals.',
  },
];
